/**
 * @fileoverview Utility functions for note management including tag normalization,
 * page title generation, note display helpers, sorting utilities, and memo comparison.
 */

import type { DeleteNoteHandler, NotesWithTags, ToggleBookmarkHandler, ToggleFlagHandler, TogglePinHandler } from '../types';

/** Input type for page title generation */
interface NoteForTitle {
  title?: string | null;
  id?: string;
}

/** Note with is_pinned property for separation */
interface NoteWithPinned {
  is_pinned: boolean;
}

/**
 * Separates notes into pinned and unpinned arrays.
 * @param notes - Array of notes with is_pinned property
 * @returns Object with pinnedNotes and unpinnedNotes arrays
 */
export function separatePinnedNotes<T extends NoteWithPinned>(notes: readonly T[]): {
  pinnedNotes: T[];
  unpinnedNotes: T[];
} {
  return {
    pinnedNotes: notes.filter(note => note.is_pinned),
    unpinnedNotes: notes.filter(note => !note.is_pinned),
  };
}

/** Input type for ID fallback generation */
interface NoteForId {
  id?: string;
}

export { normalizeTag as normalizeTagName } from '@schema/note_form';

/**
 * Generate dynamic page title based on note data
 * @param note - The note object
 * @returns Page title string
 */
export function generatePageTitle(note: NoteForTitle | null): string {
  return note ? `${note.title || 'Untitled Note'}` : 'Edit Note';
}

/**
 * Generate note ID fallback for breadcrumbs
 * @param note - The note object
 * @returns Note ID or fallback string
 */
export function generateNoteIdFallback(note: NoteForId | null): string {
  return note ? `${note.id}` : 'New Note';
}

/**
 * Truncates content to a maximum length with ellipsis.
 * @param content - The content to truncate (may be null or undefined)
 * @param maxLength - Maximum length before truncation
 * @returns Truncated content string, or empty string if content is null/undefined
 */
export function truncateContent(content: string | null | undefined, maxLength: number): string {
  if (!content) {
    return '';
  }
  return content.length > maxLength ? content.substring(0, maxLength) + '...' : content;
}

/** Options for formatDate */
interface FormatDateOptions {
  /** Include time in the output (e.g., "Jan 15, 2024, 10:30 AM") */
  includeTime?: boolean;
  /** Fallback text when date is null/undefined (default: 'Unknown date') */
  fallback?: string | null;
}

/** Tag with occurrence count for sorting */
interface TagWithOccurrence {
  name: string;
  occurrence?: number;
}

/**
 * Sorts tags by occurrence count (highest first) and maps to tag names.
 * @param tags - Array of tags with occurrence counts
 * @returns Array of tag names sorted by occurrence
 */
export function getSortedTagNames<T extends TagWithOccurrence>(tags: T[]): string[] {
  return tags
    .slice()
    .sort((a, b) => (b.occurrence ?? 0) - (a.occurrence ?? 0))
    .map((tag) => tag.name);
}

/** Item with created_at for chronological sorting */
interface HasCreatedAt {
  created_at?: string | null;
}

/**
 * Sorts items chronologically by created_at date (newest first).
 * @param items - Array of items with created_at property
 * @returns New sorted array (original is not mutated)
 */
export function sortByCreatedAtDesc<T extends HasCreatedAt>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bDate - aDate;
  });
}

/**
 * Formats a date string to a human-readable format.
 * @param dateString - ISO date string to format
 * @param options - Formatting options
 * @returns Formatted date string (e.g., "Jan 15, 2024" or "Jan 15, 2024, 10:30 AM")
 */
export function formatDate(
  dateString: string | null | undefined,
  options: FormatDateOptions = {}
): string | null {
  const { includeTime = false, fallback = 'Unknown date' } = options;

  if (!dateString) {
    return fallback;
  }

  try {
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString('en-GB', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    if (!includeTime) {
      return datePart;
    }

    const timePart = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${datePart}, ${timePart}`;
  } catch {
    return fallback;
  }
}

// =============================================================================
// Sorting Utilities
// =============================================================================

/** Item with updated_at for chronological sorting */
interface HasUpdatedAt {
  updated_at?: string | null;
}

/**
 * Sorts items chronologically by updated_at date (newest first).
 * @param items - Array of items with updated_at property
 * @returns New sorted array (original is not mutated)
 */
export function sortByUpdatedAtDesc<T extends HasUpdatedAt>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
    const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
    return bTime - aTime;
  });
}

// =============================================================================
// Memo Comparison Utilities
// =============================================================================

/** Props for notes display components (grid/list) */
export interface NotesDisplayProps {
  notes: readonly NotesWithTags[];
  onTogglePin: TogglePinHandler;
  onToggleBookmark?: ToggleBookmarkHandler;
  onToggleFlag?: ToggleFlagHandler;
  onDelete?: DeleteNoteHandler;
  basePath?: string;
}

/**
 * Comparison function for React.memo on notes display components.
 * Returns true if props are equal (component should NOT re-render).
 * @param prevProps - Previous props
 * @param nextProps - Next props
 * @returns true if props are equal
 */
export function areNotesDisplayPropsEqual(
  prevProps: NotesDisplayProps,
  nextProps: NotesDisplayProps
): boolean {
  return (
    prevProps.notes === nextProps.notes &&
    prevProps.onTogglePin === nextProps.onTogglePin &&
    prevProps.onToggleBookmark === nextProps.onToggleBookmark &&
    prevProps.onToggleFlag === nextProps.onToggleFlag &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.basePath === nextProps.basePath
  );
}